import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserCalendar.translations.json';
import React, { useMemo, useState } from 'react';
import * as Icon from 'react-feather';
import { Calendar, CalendarProps, Flex } from 'antd';
import {
  colorBgContainer,
  colorBlue,
  colorCalendarCellToday,
  colorGold,
  colorGray,
  colorGreen,
  colorText,
} from '@atlas-frontend/design-tokens';
import {
  Bullet,
  InfoIcon,
  InfoIconUrl,
  Padding,
  Spinner,
  Stack,
} from '../../common/components';
import styled from 'styled-components';
import { getDayjs, getDayjsNow } from '../../common/utils';
import { CalendarHeader, MONTH_VIEW } from '../../common/calendar';
import { keepPreviousData } from '@tanstack/react-query';
import { useGetUserCalendarQuery } from './query/useGetUserCalendarQuery';
import { Dayjs } from 'dayjs';
import { UserCalendarDto, UserCalendarExecutionType } from './schema/types';
import { UserCalendarDateCell } from './UserCalendarDateCell';

const { addTranslations, t } = i18nNsFactory('user-calendar');
addTranslations(translations);

export const UserCalendar = () => {
  const { data, isLoading } = useGetUserCalendarQuery({
    placeholderData: keepPreviousData,
  });

  const [period, setPeriod] = useState(getDayjsNow());

  const executionMap = useMemo(() => {
    const executionMap = new Map<
      string,
      Map<UserCalendarExecutionType, UserCalendarDto[]>
    >();
    (data || []).forEach((execution: UserCalendarDto) => {
      const dueDate = getDayjs(execution.dueDate)
        ?.startOf('day')
        .format('YYYY-MM-DD');
      if (!dueDate) return;
      if (!executionMap.has(dueDate)) {
        executionMap.set(dueDate, new Map());
      }
      const executionsByType = executionMap.get(dueDate);
      if (executionsByType?.has(execution.type)) {
        executionsByType?.get(execution.type)?.push(execution);
      } else {
        executionsByType?.set(execution.type, [execution]);
      }
    });
    return executionMap;
  }, [data]);

  if (isLoading) {
    return <Spinner />;
  }

  const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
    if (info.type === 'date') {
      const dueDate = getDayjs(current)?.startOf('day').format('YYYY-MM-DD');
      if (dueDate) {
        return (
          <UserCalendarDateCell executionsByType={executionMap.get(dueDate)} />
        );
      }
    }

    return (
      <div title="" className="ant-picker-cell">
        <div title="" className="ant-picker-cell ant-picker-cell-in-view">
          <div title="" className="ant-picker-cell-inner">
            {current.date()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ContentWrapper style={{ flex: 1, height: '100%' }}>
      <StyledHeader horizontal>
        <Flex gap="small" justify="space-between">
          <Flex gap="small" align="center" justify="space-between">
            <Icon.Calendar size={18} />
            <h4>{t('title')}</h4>
            <InfoIcon infoIconUrl={InfoIconUrl.USER_CALENDAR} />
          </Flex>
          <Padding top={8} style={{ height: '100%' }}>
            <CalendarHeader
              period={period}
              onChangePeriod={setPeriod}
              type={MONTH_VIEW}
              id={'user-calendar-header'}
            />
          </Padding>
        </Flex>
      </StyledHeader>
      <Padding horizontal style={{ height: '100%' }}>
        <Calendar
          value={period}
          onChange={setPeriod}
          headerRender={() => null}
          cellRender={cellRender}
          style={{ height: '100%' }}
        />
      </Padding>
      <UserCalendarFooter />
    </ContentWrapper>
  );
};

export const UserCalendarFooter = () => {
  return (
    <StyledFooter horizontal>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={6}>
          <Bullet color={colorBlue[2]} />
          {t('calendarInputDeadline.label')}
        </Flex>
        <Flex align="center" gap={6}>
          <Bullet color={colorGreen[4]} />
          {t('calendarOutputReportDeadline.label')}
        </Flex>
        <Flex align="center" gap={6}>
          <Bullet color={colorGold[6]} />
          {t('calendarOutputReportPublication.label')}
        </Flex>
      </Flex>
    </StyledFooter>
  );
};

const StyledHeader = styled(Padding)`
  border-bottom: 1px solid ${colorGray[2]};
`;

const StyledFooter = styled(Padding)`
  border-top: 1px solid ${colorGray[2]};
`;

const ContentWrapper = styled(Stack)`
  background-color: ${colorBgContainer};
  border: 1px solid ${colorGray[2]};
  gap: 0;
  .calendar-header-selection-wrapper {
    width: 250px;
  }

  .ant-picker-calendar .ant-picker-panel {
    .ant-picker-date-panel,
    .ant-picker-body,
    .ant-picker-content {
      height: 100%;
    }
    .ant-picker-cell {
      color: ${colorGray[3]};
    }
    .ant-picker-cell-in-view {
      color: ${colorText};
    }

    .ant-picker-cell:hover:not(.ant-picker-cell-today) {
      .ant-picker-cell-inner {
        background-color: transparent !important;
      }
    }
    .ant-picker-cell-today {
      .ant-picker-cell-inner {
        background-color: ${colorCalendarCellToday};
      }
    }
  }

  .ant-picker-calendar.ant-picker-calendar-full {
    height: 100%;
    .ant-picker-calendar-date-value {
      line-height: 14px;
    }
    .ant-picker-calendar-date-content {
      height: 14px;
    }
    .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-calendar-date,
    .ant-picker-cell-in-view.ant-picker-cell-selected
      .ant-picker-calendar-date-today {
      background-color: unset;
    }
    .ant-picker-cell-selected
      .ant-picker-calendar-date
      .ant-picker-calendar-date-value,
    .ant-picker-cell-selected:hover
      .ant-picker-calendar-date
      .ant-picker-calendar-date-value,
    .ant-picker-cell-selected
      .ant-picker-calendar-date-today
      .ant-picker-calendar-date-value,
    .ant-picker-cell-selected:hover
      .ant-picker-calendar-date-today
      .ant-picker-calendar-date-value {
      color: ${colorText};
    }
  }
`;
